import axios from 'axios'
import { API_URL } from '@/config'
import store from '@/store'
import router from '@/router'
import sessionToken from '@/services/sessionToken'
import clientId from '@/services/clientId'

const API = axios.create({
  baseURL: API_URL,
  timeout: 45000,
})

const INTERNAL_HEADERS = ['authorization', 'x-client-id', 'x-interaction-id', 'x-request-id']

function isInternalRequest (config) {
  try {
    const apiUrl = new URL(API_URL, window.location.origin)
    const requestUrl = new URL(config.url || '', config.baseURL || API_URL)
    return apiUrl.origin === requestUrl.origin
  } catch {
    return !(config.url && /^https?:\/\//i.test(config.url))
  }
}

function removeInternalHeaders (headers) {
  if (!headers) return
  Object.keys(headers).forEach(key => {
    if (INTERNAL_HEADERS.includes(key.toLowerCase())) {
      delete headers[key]
    } else if (headers[key] && typeof headers[key] === 'object') {
      removeInternalHeaders(headers[key])
    }
  })
}

function getResponseHeader (response, name) {
  if (!response || !response.headers) return null
  return response.headers[name.toLowerCase()] || response.headers[name] || null
}

function correlationFromResponse (response, fallback) {
  return {
    clientId: clientId.getClientId(),
    interactionId: getResponseHeader(response, 'X-Interaction-Id') || fallback.interactionId,
    requestId: getResponseHeader(response, 'X-Request-Id') || fallback.requestId,
  }
}

function isOutcomeUnknown (error) {
  const status = error.response && error.response.status
  return !error.response || error.code === 'ECONNABORTED' || [502, 503, 504].includes(status)
}

function decorateError (error) {
  const config = error.config || {}
  const correlation = correlationFromResponse(error.response, {
    interactionId: config._interactionId,
    requestId: config._requestId,
  })

  error.correlation = correlation
  error.interactionId = correlation.interactionId
  error.requestId = correlation.requestId
  error.outcomeUnknown = isOutcomeUnknown(error)
  error.errorKind = error.outcomeUnknown ? 'outcome_unknown' : 'request_failed'
  error.httpStatus = error.response && error.response.status

  if (error.response && error.response.data && typeof error.response.data === 'object') {
    const data = error.response.data
    data.request_id = correlation.requestId
    data.interaction_id = correlation.interactionId
    data.support_code = correlation.requestId
    data.outcome_unknown = error.outcomeUnknown
    data.error_kind = error.errorKind
    data.http_status = error.httpStatus
  }

  return error
}

// REQUEST: injeta X-Client-Id (telemetria por instalação) e, para chamadas
// marcadas com requiresSessionToken, garante o fts_session_token (chamando
// /auth se necessário) antes de enviar.
API.interceptors.request.use(async config => {
  config.headers = config.headers || {}
  config._isInternalRequest = isInternalRequest(config)

  if (!config._isInternalRequest) {
    removeInternalHeaders(config.headers)
    config.requiresSessionToken = false
    return config
  }

  config._interactionId = config._interactionId || config.interactionId || clientId.generateUuidV4()
  config._requestId = clientId.generateUuidV4()
  config.headers['X-Client-Id'] = clientId.getClientId()
  config.headers['X-Interaction-Id'] = config._interactionId
  config.headers['X-Request-Id'] = config._requestId

  if (config.requiresSessionToken) {
    const token = await sessionToken.ensureToken(config._interactionId)
    if (token) {
      config.headers.Authorization = token
    }
  }

  if (typeof config.onRequest === 'function') {
    config.onRequest({
      clientId: clientId.getClientId(),
      interactionId: config._interactionId,
      requestId: config._requestId,
    })
  }
  return config
})

API.interceptors.response.use(response => {
  response.correlation = correlationFromResponse(response, {
    interactionId: response.config && response.config._interactionId,
    requestId: response.config && response.config._requestId,
  })
  return response
}, async error => {
  const original = error.config
  const status = error.response?.status
  const data = error.response?.data
  const code = data?.code
  const shouldHandleInternalAuth = original && original._isInternalRequest !== false

  // Se a request exigia session token e bateu 401, tenta uma única vez
  // renovar o cookie via /auth e refazer a request original.
  if (status === 401 && shouldHandleInternalAuth && original.requiresSessionToken && !original._retriedSession) {
    original._retriedSession = true
    original._interactionId = getResponseHeader(error.response, 'X-Interaction-Id') || original._interactionId
    sessionToken.clearToken()
    const fresh = await sessionToken.ensureToken(original._interactionId)
    if (fresh) {
      original.headers = original.headers || {}
      original.headers.Authorization = fresh
      return API(original)
    }
  }

  // 403 + sistema_bloqueado | web_nao_liberada. O destino depende do momento, não do código:
  // - sem sessão (autenticação do web ou do bot) → a própria tela mostra a mensagem inline,
  //   preservando o contexto pro cliente corrigir o CNPJ ou acionar o suporte
  // - com sessão (cliente navegando perdeu o acesso) → derruba a sessão e manda pra conta bloqueada
  const isBlockedCode = code === 'sistema_bloqueado' || code === 'web_nao_liberada'
  if (shouldHandleInternalAuth && status === 403 && isBlockedCode && store.state.auth.token) {
    sessionStorage.setItem('blockedReason', JSON.stringify({ code, message: data?.message }))
    store.dispatch('auth/logout')
    router.replace({ name: 'auth.blocked' }).catch(() => {})
    return Promise.reject(decorateError(error))
  }

  // 403 + pedido_nao_habilitado → não derruba sessão; UI trata via setFeedback
  if (shouldHandleInternalAuth && status === 403 && code === 'pedido_nao_habilitado') {
    return Promise.reject(decorateError(error))
  }

  // 401 (token_invalido ou legado) → login
  if (shouldHandleInternalAuth && status === 401) {
    store.dispatch('auth/logout')

    router.replace({ name: 'auth.login' })
      .catch(() => {})
  }
  return Promise.reject(decorateError(error))
})

const setToken = (token) => {
  API.defaults.headers.common.Authorization = token
}

const unsetToken = () => {
  delete API.defaults.headers.common.Authorization
}

const deleteLocalStorage = () => {
  localStorage.removeItem('vuex')
}

const call = ({ method, headers, data, url, success, error, requiresSessionToken, responseType, timeout, interactionId, onRequest }) => {
  const options = {
    headers,
    method,
    url,
    data,
    responseType,
    requiresSessionToken,
    timeout,
    interactionId,
    onRequest,
  }

  return API(options)
    .then(success)
    .catch((err) => {
      return error ? error(err) : Promise.reject(err)
    })
}

var crypto = require('crypto-browserify')

const algorithm = 'aes-256-ctr'
const password = 'fcdca36e6c259f521fddef3d7dcc67b2'

function encrypt (text) {
  const IV = crypto.randomBytes(16)
  const cipher = crypto.createCipheriv(algorithm, password, IV)
  let crypted = IV.toString('hex') + cipher.update(text, 'utf8', 'hex')
  crypted += cipher.final('hex')
  return crypted
}

function decrypt (text) {
  if (text == null || text.length < 32) {
    return null
  }
  const IV = Buffer.from(text.substr(0, 32), 'hex')
  const encrypted = text.substring(32)
  const decipher = crypto.createDecipheriv(algorithm, password, IV)
  let decrypted = decipher.update(encrypted, 'hex', 'utf8')
  decrypted += decipher.final('utf8')
  return decrypted
}

export default {
  call,
  setToken,
  unsetToken,
  deleteLocalStorage,
  encrypt,
  decrypt,
}
